/**
 * Pure calendar-grid logic: which cells a month shows and what state each day
 * is in. No React in here, so it is testable with plain values in node — the
 * component only maps the result to markup.
 */

import { compareDates, type PlainDate, plainDate } from "@bivak/domain";

export type DayState = "vrij" | "bezet" | "wissel" | "eigen";

export type DayCell = {
  date: PlainDate;
  /** Days padding the first and last week belong to a neighbouring month. */
  inMonth: boolean;
  /** Days before today: not bookable, and outside the fetched window — the
   *  occupancy that would paint them was never loaded, so showing them as
   *  "vrij" would be a lie. Rendered muted, like padding days. */
  past: boolean;
  weekend: boolean;
  state: DayState;
};

type Range = { from: string; to: string };
type OccupancyRow = Range & { resourceId: string; kind: "rental" | "blackout" | "maintenance" };

/** Half-open [from, to): does `day` fall inside? */
function covers(range: Range, day: string): boolean {
  return range.from <= day && day < range.to;
}

/**
 * The state of one calendar day for one space.
 *
 * Precedence: a rental beats eigen werking beats a wisseldag. `wissel` marks a
 * day that is only the *end* of a closure — departure is at noon, so the same
 * day can be someone else's arrival, and painting it fully closed would hide
 * real weekends. Blackouts and maintenance render as "eigen werking": to a
 * renter the difference between the chiro's own camp and painting week is
 * irrelevant, and neither names anyone (NFR-6).
 */
export function dayState(
  day: PlainDate,
  spaceResourceIds: readonly string[],
  occupancy: readonly OccupancyRow[],
): DayState {
  // A space composed of nothing cannot be let — same rule as the domain's
  // unavailableRanges. Showing it free would offer a booking the database
  // refuses to hold.
  if (spaceResourceIds.length === 0) return "bezet";

  const iso = day.toString();
  const resources = new Set(spaceResourceIds);
  const relevant = occupancy.filter((row) => resources.has(row.resourceId));

  if (relevant.some((row) => row.kind === "rental" && covers(row, iso))) return "bezet";
  if (relevant.some((row) => row.kind !== "rental" && covers(row, iso))) return "eigen";
  if (relevant.some((row) => row.to === iso)) return "wissel";
  return "vrij";
}

/**
 * The cells of a month grid, Monday-first, padded to whole weeks.
 *
 * Worked example: September 2026 starts on a Tuesday, so the grid opens with
 * Monday 31 August (inMonth: false) and runs 35 cells to Sunday 4 October.
 */
export function monthCells(
  month: PlainDate,
  spaceResourceIds: readonly string[],
  occupancy: readonly OccupancyRow[],
  todayIso?: string,
): DayCell[] {
  const first = month.with({ day: 1 });
  const start = first.subtract({ days: first.dayOfWeek - 1 });
  const daysInMonth = first.daysInMonth;
  const weeks = Math.ceil((first.dayOfWeek - 1 + daysInMonth) / 7);

  const cells: DayCell[] = [];
  for (let index = 0; index < weeks * 7; index++) {
    const date = start.add({ days: index });
    cells.push({
      date,
      inMonth: date.month === first.month && date.year === first.year,
      past: todayIso !== undefined && date.toString() < todayIso,
      weekend: date.dayOfWeek >= 5,
      state: dayState(date, spaceResourceIds, occupancy),
    });
  }
  return cells;
}

/**
 * Weeks worth rendering: leading weeks that are entirely past or padding carry
 * no information, and on a phone the current month opened with up to three dead
 * rows before the first bookable day (design review, 2026-08-21). Whole weeks
 * only — dropping partial rows would break the weekday-column alignment. Never
 * trims below TWO weeks: on the last day of a month the maximal trim left one
 * live number in a row of blanks, which read as a rendering bug, and a wholly
 * past month must stay a month, not an empty card (review find).
 */
export function zichtbareWeken(cells: readonly DayCell[]): DayCell[] {
  let start = 0;
  // start+21: advancing by a week must still leave 14 cells behind it.
  while (
    start + 21 <= cells.length &&
    cells.slice(start, start + 7).every((cell) => !cell.inMonth || cell.past)
  ) {
    start += 7;
  }
  return cells.slice(start);
}

/** The first day of the month `offset` months after today's month. */
export function monthStart(todayIso: string, offset: number): PlainDate {
  return plainDate(todayIso).with({ day: 1 }).add({ months: offset });
}

/** Keep a paging offset inside the fetched window. */
export function clampOffset(offset: number, monthsVisible: number, monthsFetched: number): number {
  return Math.max(0, Math.min(offset, monthsFetched - monthsVisible));
}

export { compareDates };
